import { useEffect, useState, useRef, useCallback } from "react";

import screenfull from "~/utils/SSRScreenFull";

import styled from "styled-components";
import ReactPlayer from "react-player/lazy";
import debounce from "lodash/debounce";

import { LoadingIcon } from "../LoadingIcon";
import { AspectRatio } from "../AspectRatio";

import { PlayerSVGs } from "./PlayerSVGs";

import { primaryInput } from "detect-it";
import useIsMounted from "~/hooks/useIsMounted";
import { useHeaderContext } from "~/providers/HeaderContextProvider";
import { useConfigContext } from "~/providers/ConfigContextProvider";

export type VideoPlayerOptions = {
  autoPlay?: boolean;
  muted?: boolean;
  looped?: boolean;
  playing?: boolean;
  onPlay?: Function;
  onPause?: Function;
  onShowUi?: Function;
  onHideUi?: Function;
  onClickPreview?: Function;
};

type VideoPlayerProgressInfo = {
  playedSeconds: number;
  played: number;
  loadedSeconds: number;
  loaded: number;
};

const uiHideDelay = 2000;

const convertSeconds = (seconds: number) => {
  var convert = (x: number) => (x < 10 ? "0" + x.toFixed(0) : x.toFixed(0));

  if (seconds < 60) return `00:${convert(Math.floor(seconds % 60))}`;

  if (seconds < 3600)
    return `${convert(Math.floor((seconds / 60) % 60))}:${convert(
      Math.floor(seconds % 60)
    )}`;

  return (
    convert(Math.floor(seconds / (60 * 60))) +
    ":" +
    convert(Math.floor((seconds / 60) % 60)) +
    ":" +
    convert(Math.floor(seconds % 60))
  );
};

const VideoPlayerContainer = styled.div`
  position: relative;
  width: 100%;
  background: #000;
  height: 100%;

  .video-player.error {
    display: flex;
    height: 100%;
    width: 100%;
    justify-content: center;
    align-items: center;
    color: #fff;
  }

  .react-player {
    iframe {
      object-fit: none;
      object-position: top left;
    }
  }

  video::cue {
    color: #fff;
    font-family: "ABC Diatype", Arial, Helvetica, sans-serif;
    line-height: 1.3em;
  }
`;

const UIButtonIcon = styled.div<{ active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: opacity 0.2s;
`;

const UIButton = styled.button`
  position: relative;
  width: 30px;
  height: 30px;
  margin: 0 10px;

  border: none;
  background: none;
  cursor: pointer;

  & > div {
    filter: invert(100%);
    transition: filter 0.3s;
  }
  &:active,
  &:focus-within {
    & > div {
      filter: invert(66.66%);
    }
  }

  @media (any-pointer: fine) {
    &:hover {
      & > div {
        filter: invert(66.66%);
      }
    }
  }

  ${({ theme }) => theme.breakpoints.tablet} {
    width: 50px;
    height: 50px;
  }
`;

const UIBigPlayIcon = styled(UIButton)`
  position: relative;
  margin: 0;
  padding: 0;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        height: ${props.theme.spacePx(breakpoint, 5)};
        width: ${props.theme.spacePx(breakpoint, 5)};
        `;
    })}
`;

const UIText = styled.div`
  display: flex;
  align-items: flex-start;
  height: 15px;
  color: #fff;
  line-height: 12px;
  font-size: 12px;
  padding: 0 3px;

  ${({ theme }) => theme.breakpoints.tablet} {
    height: 25px;
    line-height: 16px;
    font-size: 16px;
    align-items: center !important;
  }
`;

const UIProgressBar = styled.div`
  position: relative;
  height: 30px;
  margin: 0 10px;
  border: none;
  background: none;
  flex-grow: 10;

  & progress {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 100%;
    height: 2px;
    border: none;
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.8);
    -webkit-appearance: none;

    &::-webkit-progress-bar {
      background-color: rgba(255, 255, 255, 0.8);
    }

    &::-webkit-progress-value {
      background-color: #fff;
    }

    &::-moz-progress-bar {
      background-color: #fff;
    }
  }

  & input {
    z-index: 2;
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
    transform: translateY(-50%);
    cursor: pointer;
    -webkit-appearance: none;
    margin: 0;
    background-color: rgba(255, 255, 255, 0);
    box-shadow: none;
    border: none;

    &::-moz-range-thumb {
      width: 2px;
      height: 30px;
      box-shadow: none;
      border: none;
      background-color: rgba(255, 255, 255, 0);
    }
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 2px;
      height: 30px;
      background-color: rgba(255, 255, 255, 0);
    }

    &:active {
      &::-webkit-slider-thumb {
        background-color: rgba(255, 255, 255, 1);
      }
      &::-moz-range-thumb {
        background-color: rgba(255, 255, 255, 1);
      }
    }
  }

  ${({ theme }) => theme.breakpoints.tablet} {
    width: 50px;
    height: 50px;

    & input {
      &::-moz-range-thumb {
        height: 50px;
      }
      &::-webkit-slider-thumb {
        height: 50px;
      }
    }
  }
`;

const UI = styled.div<{ active: boolean }>`
  position: absolute;
  left: 5px;
  bottom: 15px;
  width: calc(100% - 10px);
  display: flex;
  transition: opacity 0.5s;
  opacity: ${({ active }) => (active ? 1 : 0)};
  pointer-events: ${({ active }) => (active ? "auto" : "none")};

  ${({ theme }) => theme.breakpoints.tablet} {
    left: 10px;
    bottom: 20px;
    width: calc(100% - 20px);
  }
`;

const Label = styled.label`
  display: inline-block;
  width: 0px;
  height: 0px;
  overflow: hidden;
  position: absolute;
  top: -100%;
`;

const UIProgressState = styled.div.attrs<{
  progress: number;
}>((props) => ({
  style: {
    left:
      props.progress < 50
        ? `${props.progress.toFixed(3)}%`
        : `${(props.progress - 50).toFixed(3)}%`,
  },
}))<{
  progress: number;
}>`
  position: relative;
  display: flex;
  width: 50%;
  font-size: 12px;
  height: 100%;
  top: 0;
  justify-content: ${({ progress }) =>
    progress < 50 ? "flex-start" : "flex-end"};
  align-content: flex-start;

  border-left: ${({ progress }) => (progress < 50 ? "2px solid #fff" : "none")};
  border-right: ${({ progress }) =>
    progress >= 50 ? "2px solid #fff" : "none"};
  color: #fff;
`;

const uA =
  typeof navigator !== "undefined" ? navigator.userAgent.toLowerCase() : "";
const isWindows = uA.indexOf("windows") !== -1;
const isChrome = uA.indexOf("chrome") !== -1;
const isSafari =
  (uA.indexOf("safari") !== -1 ||
    uA.indexOf("applewebkit") !== -1 ||
    uA.indexOf("ipad") !== -1 ||
    uA.indexOf("iphone") !== -1) &&
  !isChrome;

const createIdPartial = () => {
  return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);
};

// Better typings
export const VideoPlayer = ({
  src,
  captions,
  posterSizes,
  options,
  id,
  vimeoId,
  type,
  mode,
  previewComponent,
}: {
  src: Record<number, string> | Record<string, string> | string;
  posterSizes?: any;
  captions?: any;
  vimeoId?: any;
  options?: VideoPlayerOptions;
  id?: string | number;
  type: string;
  mode?: "fill" | undefined;
  previewComponent?: any;
}) => {
  const config = useConfigContext();
  const headerContext = useHeaderContext();
  const videoIsPlayingRef = useRef(!!options?.autoPlay);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<ReactPlayer>(null);

  const isFullscreenRef = useRef<boolean>(false);
  const hasCaptionsRef = useRef<boolean>(false);
  const captionsTuckedUpRef = useRef<boolean>(false);
  const hasDelayPassedRef = useRef<boolean>(false);

  const uiHideTimoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isMounted = useIsMounted();
  const containerClasses = ["video-player", "has-loading-icon"];

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUi, setShowUi] = useState(true);
  const [seeking, setSeeking] = useState(false);
  const [tracks, setTracks] = useState<any[]>([]);
  const [error, setError] = useState(false);
  const [hasCaptions, setHasCaptions] = useState(false);
  const [showCaptions, setShowCaptions] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);

  const [playing, setPlaying] = useState(
    !!options?.autoPlay || !!options?.playing
  );
  const [buffering, setBuffering] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(!!options?.autoPlay || !!options?.muted);
  const [url, setUrl] = useState<string | string[] | undefined>();
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState<VideoPlayerProgressInfo>({
    playedSeconds: 0,
    played: 0,
    loadedSeconds: 0,
    loaded: 0,
  });
  const [duration, setDuration] = useState(0);
  const [currentQuality, setCurrentQuality] = useState(0);
  const [isIOS, setIsIOS] = useState(false);
  const [videoId, setVideoId] = useState("");
  const scrollPositionRef = useRef({
    x: 0,
    y: 0,
  });

  const [previewClicked, setPreviewClicked] = useState(
    !["video", "vimeo_api"].includes(type)
  );

  const [posterUrl, setPosterUrl] = useState<string | boolean>(false);

  if (loading) containerClasses.push("loading");

  if (!loading) containerClasses.push("loaded");

  if (isFullscreen) containerClasses.push("fullscreen");

  if (showUi || seeking) containerClasses.push("showUi");

  if (id) containerClasses.push(`player-${id}`);

  const setPlayingState = (p: boolean) => {
    setPlaying(p);
    videoIsPlayingRef.current = p;
  };

  const resetUiTimeout = () => {
    if (isMounted && type !== "vimeo_url") {
      setShowUi(true);

      if (hasCaptionsRef.current && !captionsTuckedUpRef.current) {
        const video = videoRef.current?.getInternalPlayer();
        if (video?.textTracks?.length && video?.textTracks?.[0]?.cues?.length) {
          Array.from(video?.textTracks[0].cues).forEach((c) => {
            (c as VTTCue).line = -4;
          });
        }
        captionsTuckedUpRef.current = true;
      }

      if (typeof options?.onShowUi === "function") options.onShowUi.call(null);

      if (uiHideTimoutRef.current) clearTimeout(uiHideTimoutRef.current);
      uiHideTimoutRef.current = setTimeout(() => {
        setShowUi(false);

        if (hasCaptionsRef.current && captionsTuckedUpRef.current) {
          const video = videoRef.current?.getInternalPlayer();

          if (
            video?.textTracks?.length &&
            video?.textTracks?.[0]?.cues?.length
          ) {
            Array.from(video?.textTracks[0].cues).forEach((c) => {
              (c as VTTCue).line = -2;
            });
          }
          captionsTuckedUpRef.current = false;
        }

        if (typeof options?.onHideUi === "function")
          options.onHideUi.call(null);
      }, uiHideDelay);
    }
  };

  const onFullscreenEnter = () => {
    isFullscreenRef.current = true;
    setIsFullscreen(true);
    headerContext.setObserveScroll(false);
    scrollPositionRef.current = {
      y: window.scrollY,
      x: window.scrollX,
    };
  };

  const onFullscreenExit = () => {
    isFullscreenRef.current = false;
    setIsFullscreen(false);
    if (isWindows || isChrome) {
      setTimeout(() => {
        window.scrollTo({
          top: scrollPositionRef.current.y,
          left: scrollPositionRef.current.x,
        });
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("scroll"));
        }, 150);
      }, 150);
    }
    setTimeout(() => {
      headerContext.setObserveScroll(true);
    }, 500);
  };

  const toggleFullscreen = async () => {
    if (!videoContainerRef.current) return;

    if (!isIOS) {
      if (isFullscreen) {
        try {
          if (typeof screen?.orientation?.unlock === "function")
            screen?.orientation?.unlock();
        } finally {
          screenfull.exit();
        }
      } else {
        onFullscreenEnter();
        try {
          if (typeof screen?.orientation?.lock === "function") {
            await screen?.orientation?.lock("landscape");
          }
        } catch (err) {}
        screenfull.request(videoContainerRef.current);
      }
    } else {
      const video = videoContainerRef.current.querySelector("video");
      const hasBeenPlaying = playing;
      if (video && (video as any).webkitSupportsFullscreen) {
        if (!isFullscreen) {
          headerContext.setObserveScroll(false);
          try {
            if (typeof screen?.orientation?.lock === "function") {
              await screen?.orientation?.lock("landscape");
            }
            (video as any).webkitEnterFullscreen();
          } catch (err) {}
        } else {
          headerContext.setObserveScroll(true);
          try {
            if (typeof screen?.orientation?.unlock === "function")
              screen?.orientation?.unlock();
          } finally {
            (video as any).webkitExitFullscreen();

            setTimeout(() => {
              headerContext.setObserveScroll(true);

              if (hasBeenPlaying) {
                setPlaying(true);
              }
            }, 500);
          }
        }
        setIsFullscreen(!isFullscreen);
      }
    }
  };

  const onPointerMove = () => {
    if (videoIsPlayingRef.current && isMounted) {
      resetUiTimeout();
    }
  };

  const toggleCaptions = () => {
    if (!hasCaptions) return;

    const video = videoRef.current?.getInternalPlayer();

    if (video?.textTracks?.length) {
      if (showCaptions) {
        video.textTracks[0].mode = "hidden";
      } else {
        video.textTracks[0].mode = "showing";
        resetUiTimeout();
      }
    }

    setShowCaptions(!showCaptions);
  };
  const onResize = useCallback(() => {
    if (typeof window === "undefined" || !isMounted) return;

    if (typeof src === "object" && Object.keys(src).length) {
      if ("hls" in src) delete src["hls"];

      const activeSrcSizes = Object.keys(src)
        .sort((s1: any, s2: any) => parseInt(s1) - parseInt(s2))
        .reduce((carry: any, size: any) => {
          if (size === "hls") return carry;
          if (
            typeof (src as any)[size] !== "object" ||
            !(src as any)[size]?.url
          )
            return carry;

          return {
            ...carry,
            [size]: (src as any)[size],
          };
        }, {});

      let firstFit = parseInt(
        Object.keys(activeSrcSizes)[Object.keys(activeSrcSizes).length - 1]
      );

      const bestFit: number = Object.keys(activeSrcSizes).reduce(
        (bF: number, size: any) => {
          const s = parseInt(size);
          if ((videoContainerRef?.current?.offsetWidth ?? 0) <= s && bF < s)
            return s;

          return bF;
        },
        0
      );

      if (bestFit) {
        if (currentQuality > bestFit || !url) {
          setUrl((src as any)[bestFit]["url"]);
          setCurrentQuality(bestFit);
        }
      } else {
        if (currentQuality > firstFit || !url) {
          setUrl((src as any)[firstFit]["url"]);
          setCurrentQuality(firstFit);
        }
      }

      if (!firstFit) setError(true);
    }

    if (typeof posterSizes === "object" && Object.keys(posterSizes).length) {
      const sizes = Object.keys(posterSizes).reduce(
        (carry: any, key: string) => {
          if (key.indexOf("-") !== -1) return carry;

          return {
            ...carry,
            [parseInt(posterSizes[`${key}-width`])]: posterSizes[key],
          };
        },
        {}
      );

      if (Object.keys(sizes).length > 0) {
        let firstFit = parseInt(
          Object.keys(sizes).sort(
            (s1: any, s2: any) => parseInt(s1) - parseInt(s2)
          )[Object.keys(sizes).length - 1]
        );

        const bestFit: number = Object.keys(sizes)
          .sort((s1: any, s2: any) => parseInt(s1) - parseInt(s2))
          .reduce((bF: number, size: any) => {
            const s = parseInt(size);
            if ((videoContainerRef?.current?.offsetWidth ?? 0) <= s && bF < s)
              return s;

            return bF;
          }, 0);
        if (bestFit) {
          if (currentQuality > bestFit || !posterUrl) {
            setPosterUrl((sizes as any)[bestFit]);
          }
        } else {
          if (currentQuality > firstFit || !posterUrl) {
            setPosterUrl((sizes as any)[firstFit]);
          }
        }
      }
    }
  }, [src, currentQuality, url, setError, isMounted, posterUrl, posterSizes]);
  const onResizeDebounced = debounce(onResize, 350);

  useEffect(() => {
    if (!isMounted) return;

    if (typeof src === "string") {
      setCurrentQuality(0);
      setUrl(src);
      setLoading(true);
    }

    if (typeof posterSizes === "string") {
      setPosterUrl(posterSizes);
    }

    if (typeof src === "object" || typeof posterSizes === "object") {
      onResize();
    }
  }, [src, onResize, isMounted, posterSizes]);

  useEffect(() => {
    setLoading(true);
  }, [url]);

  useEffect(() => {
    if (captions?.length) {
      setHasCaptions(true);
      hasCaptionsRef.current = true;
      setTracks([
        {
          kind: "subtitles",
          src: `${config.cmsUrl}/fluxed-avvf/texttrack/${vimeoId}/`,
          srcLang: "en",
          label: "EN",
          default: true,
          mode: "hidden",
        },
      ]);
    } else {
      hasCaptionsRef.current = false;
      setHasCaptions(false);
      setTracks([]);
    }
  }, [captions, vimeoId, config.cmsUrl]);

  useEffect(() => {
    onResize();

    setVideoId(`${id}-${createIdPartial()}-${createIdPartial()}`);

    if (typeof window === "undefined") return;

    let isIOSDevice = false;

    const screenfullCallbackChange = (event: any) => {
      if (!isMounted) return;

      setIsFullscreen((screenfull as any)?.isFullscreen);

      if (!(screenfull as any)?.isFullscreen && isFullscreenRef.current) {
        onFullscreenExit();
      }
    };
    const screenfullCallbackError = (event: any) => {
      if (!isMounted) return;
      setIsFullscreen(false);
      isFullscreenRef.current = false;
    };

    if ((isSafari && primaryInput === "touch") || typeof screenfull?.on !== "function") {
      setIsIOS(true);
      isIOSDevice = true;
    } else {
      screenfull.on("change", screenfullCallbackChange);
      screenfull.on("error", screenfullCallbackError);
    }

    window.addEventListener("resize", onResizeDebounced);
    window.addEventListener("pointermove", onPointerMove);

    onResize();

    const triggerOnResize = () => {
      onResize();
    };
    document.addEventListener("DOMContentLoaded", triggerOnResize);

    setTimeout(() => {
      hasDelayPassedRef.current = true;
    }, 300);

    return () => {
      window.removeEventListener("resize", onResizeDebounced);
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("DOMContentLoaded", triggerOnResize);

      if (!isIOSDevice) {
        screenfull.off("change", screenfullCallbackChange);
        screenfull.off("error", screenfullCallbackError);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <VideoPlayerContainer
        className={containerClasses.join(" ")}
        ref={videoContainerRef}
      >
        <div
          className="player-wrapper"
          style={{
            height: "100%",
          }}
        >
          <AspectRatio
            ratio="16 / 9"
            mode={
              (isFullscreen && !isIOS) || mode === "fill" ? "fill" : "constrain"
            }
            objectFit={isFullscreen || mode === "fill" ? "contain" : "cover"}
          >
            {url && !error ? (
              <div
                onMouseMove={() => {
                  if (!showUi) {
                    if (uiHideTimoutRef.current)
                      clearTimeout(uiHideTimoutRef.current);
                    setShowUi(true);
                  }
                }}
                onClick={() => {
                  if (!hasPlayed && !hasDelayPassedRef.current) return;

                  if (!showUi) {
                    if (uiHideTimoutRef.current)
                      clearTimeout(uiHideTimoutRef.current);
                    setShowUi(true);
                  } else {
                    if (playing) {
                      setPlayingState(false);
                      if (uiHideTimoutRef.current)
                        clearTimeout(uiHideTimoutRef.current);
                      setShowUi(true);
                    } else {
                      setPlayingState(true);
                      resetUiTimeout();
                    }
                  }
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                }}
              >
                <ReactPlayer
                  ref={videoRef}
                  className="react-player"
                  width="100%"
                  height="100%"
                  controls={type === "youtube_url"}
                  url={url}
                  autoPlay={false}
                  playing={playing}
                  loop={options?.looped}
                  volume={volume}
                  muted={muted}
                  light={posterUrl}
                  onReady={() => {
                    setLoading(false);
                    setBuffering(false);
                    setSeeking(false);
                    if (playing && !ended) resetUiTimeout();

                    if (!hasCaptions) return;

                    const video = videoRef.current?.getInternalPlayer();

                    if (video?.textTracks?.length) {
                      video.textTracks[0].mode = "hidden";
                    }
                  }}
                  onStart={() => {
                    setPlayingState(true);
                    setBuffering(false);
                    setSeeking(false);
                    if (playing && !ended) resetUiTimeout();
                  }}
                  onPlay={() => {
                    setPlayingState(true);
                    setBuffering(false);
                    setSeeking(false);
                    setEnded(false);
                    setShowUi(true);
                    setHasPlayed(true);
                    resetUiTimeout();

                    if (typeof options?.onPlay === "function")
                      options.onPlay.call(null);

                    if (typeof options?.onShowUi === "function")
                      options.onShowUi.call(null);
                  }}
                  onPause={() => {
                    if (uiHideTimoutRef.current)
                      clearTimeout(uiHideTimoutRef.current);
                    setShowUi(true);

                    if (typeof options?.onPause === "function")
                      options.onPause.call(null);

                    if (typeof options?.onShowUi === "function")
                      options.onShowUi.call(null);

                    setBuffering(false);
                    setSeeking(false);
                  }}
                  onBuffer={() => {
                    if (playing) setBuffering(true);
                  }}
                  onBufferEnd={() => {
                    setBuffering(false);
                  }}
                  onClickPreview={() => {
                    setPreviewClicked(true);

                    if (typeof options?.onClickPreview === "function")
                      options.onClickPreview.call(null);
                  }}
                  onSeek={(e) => {
                    setSeeking(true);
                    videoIsPlayingRef.current = false;
                    if (uiHideTimoutRef.current)
                      clearTimeout(uiHideTimoutRef.current);
                    setShowUi(true);
                    if (typeof options?.onShowUi === "function")
                      options.onShowUi.call(null);
                  }}
                  onEnded={() => {
                    setPlayingState(false);
                    setEnded(true);
                    if (uiHideTimoutRef.current)
                      clearTimeout(uiHideTimoutRef.current);
                    setShowUi(true);
                    if (typeof options?.onShowUi === "function")
                      options.onShowUi.call(null);
                  }}
                  onError={(e) => {
                    console.error("VideoPlayer error:", e, e.message);
                  }}
                  onProgress={(e) => {
                    setProgress(e);
                  }}
                  onDuration={(e) => {
                    setDuration(e);
                  }}
                  playIcon={
                    previewComponent ?? (
                      <UIBigPlayIcon>
                        <UIButtonIcon
                          aria-label="Play video"
                          active={true}
                          style={{
                            backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                              "play"
                            )}')`,
                          }}
                        />
                      </UIBigPlayIcon>
                    )
                  }
                  config={{
                    file: {
                      attributes: {
                        controls: false,
                        playsInline: true,
                        id: videoId,
                        crossOrigin: "true",
                      },
                      tracks,
                    },
                    vimeo: {
                      playerOptions: {
                        background: false,
                      },
                    },
                    youtube: {
                      playerVars: {
                        playsinline: 1,
                      },
                    },
                  }}
                >
                  <video
                    width="100%"
                    height="100%"
                    controls={true}
                    src={url as string}
                  />
                </ReactPlayer>
              </div>
            ) : (
              <div className="video-player error">No video files found.</div>
            )}
          </AspectRatio>
        </div>
        <UI active={showUi && type !== "youtube_url" && previewClicked}>
          <UIButton
            onClick={() => {
              if (playing) {
                setPlayingState(false);
                if (uiHideTimoutRef.current)
                  clearTimeout(uiHideTimoutRef.current);
                setShowUi(true);
              } else {
                setPlayingState(true);
                resetUiTimeout();
              }
            }}
            aria-label={playing ? "pause video" : "play video"}
          >
            <UIButtonIcon
              active={!!!playing}
              style={{
                backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                  "play"
                )}')`,
              }}
            />
            <UIButtonIcon
              active={!!playing}
              style={{
                backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                  "pause"
                )}')`,
              }}
            />
          </UIButton>
          <UIProgressBar>
            <UIText
              style={{
                position: "absolute",
                right: 0,
                top: "calc(50% + 1px)",
                alignItems: "flex-end",
                padding: "0 5px",
              }}
            >
              {convertSeconds(duration ?? 0)}
            </UIText>

            <progress max="1" value={progress.loaded} />
            <Label htmlFor={`progess-${videoId}`}>video pogress</Label>
            <input
              id={`progess-${videoId}`}
              type="range"
              min="0"
              max="100"
              defaultValue="0"
              step={1}
              onChange={(event) => {
                if (duration && event.target.value) {
                  videoRef?.current?.seekTo(
                    ((event.target.value as any) / 100) * duration
                  );
                }
              }}
            />

            <UIProgressState progress={progress.played * 100}>
              <UIText
                style={{
                  padding: "0 3px",
                }}
              >
                {convertSeconds(progress.playedSeconds ?? 0)}
              </UIText>
            </UIProgressState>
          </UIProgressBar>

          <UIButton
            onClick={() => {
              setMuted(!muted);
            }}
            aria-label={muted ? "unmute soud" : "mute soud"}
          >
            <UIButtonIcon
              active={muted}
              style={{
                backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                  "mute"
                )}')`,
              }}
            />
            <UIButtonIcon
              active={!muted}
              style={{
                backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                  "unmute"
                )}')`,
              }}
            />
          </UIButton>
          {hasCaptions && (
            <UIButton
              onClick={toggleCaptions}
              aria-label={showCaptions ? "hide captions" : "show captions"}
            >
              <UIButtonIcon
                active={!showCaptions}
                style={{
                  backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                    "cc"
                  )}')`,
                }}
              />
              <UIButtonIcon
                active={showCaptions}
                style={{
                  filter: "invert(75%)",
                  backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                    "cc"
                  )}')`,
                }}
              />
            </UIButton>
          )}
          {(!isIOS || (isIOS && typeof src === "object")) && (
            <UIButton
              onClick={toggleFullscreen}
              aria-label={
                isFullscreen ? "leave fullscreen mode" : "enter fullscreen mode"
              }
            >
              <UIButtonIcon
                active={!isFullscreen || isIOS}
                style={{
                  backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                    "fullscreenOn"
                  )}')`,
                }}
              />
              <UIButtonIcon
                active={isFullscreen && !isIOS}
                style={{
                  backgroundImage: `url('data:image/svg+xml,${PlayerSVGs(
                    "fullscreenOff"
                  )}')`,
                }}
              />
            </UIButton>
          )}
        </UI>
        {previewClicked && (
          <LoadingIcon
            loading={
              (loading || buffering) &&
              type !== "vimeo_url" &&
              type !== "youtube_url"
            }
            color="#fff"
          />
        )}
      </VideoPlayerContainer>
    </>
  );
};
export default VideoPlayer;
